import React, { useContext, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { AppContext } from "../context/AppContext";

/**
 * Persistent dine-in session indicator.
 *
 * When the customer arrives via a table QR (selectedArea / selectedTable set in
 * AppContext), this slim bar is shown on every page — it's rendered inside the
 * sticky header wrapper, so it stays pinned as they browse. Tapping it opens a
 * confirmation to close the dine-in session, which clears the table/area context.
 *
 * Renders nothing when there is no active dine-in context.
 */
const DineInBanner = () => {
  const { selectedTable, selectedArea, setSelectedTable, setSelectedArea } =
    useContext(AppContext);
  const [showExit, setShowExit] = useState(false);

  if (!selectedTable && !selectedArea) return null;

  const areaLabel = selectedArea
    ? selectedArea.charAt(0).toUpperCase() + selectedArea.slice(1)
    : "";
  const locationText = [areaLabel, selectedTable ? `Table ${selectedTable}` : null]
    .filter(Boolean)
    .join(" · ");

  const closeDineIn = () => {
    setSelectedTable(null);
    setSelectedArea(null);
    setShowExit(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setShowExit(true)}
        aria-label={`Dine-in at ${locationText}. Tap to exit dine-in.`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          width: "100%",
          border: "none",
          cursor: "pointer",
          background: "#f9bc03",
          color: "var(--color-secondary)",
          padding: "8px 16px",
          font: "inherit",
          textAlign: "left",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <span style={{ fontSize: 18, flex: "0 0 auto" }} aria-hidden="true">
            🍽️
          </span>
          <span
            style={{
              display: "flex",
              flexDirection: "column",
              lineHeight: 1.2,
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                opacity: 0.7,
              }}
            >
              Dine-in
            </span>
            <span
              style={{
                fontSize: 14,
                fontWeight: 700,
                textTransform: "capitalize",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {locationText}
            </span>
          </span>
        </span>
        <span
          style={{
            flex: "0 0 auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            fontSize: 12,
            fontWeight: 700,
            background: "rgba(27,83,84,0.12)",
            padding: "5px 12px",
            borderRadius: 20,
          }}
        >
          Exit ✕
        </span>
      </button>

      <Modal show={showExit} onHide={() => setShowExit(false)} centered>
        <Modal.Body style={{ textAlign: "center", padding: "26px 22px" }}>
          <div style={{ fontSize: 34, marginBottom: 6 }} aria-hidden="true">
            🍽️
          </div>
          <h5 style={{ color: "var(--color-secondary)", fontWeight: 700, marginBottom: 4 }}>
            You're dining in
          </h5>
          <p
            style={{
              color: "var(--color-secondary)",
              fontWeight: 700,
              fontSize: 16,
              textTransform: "capitalize",
              marginBottom: 12,
            }}
          >
            {locationText}
          </p>
          <p
            style={{
              color: "var(--color-gray-600)",
              fontSize: 14,
              lineHeight: 1.5,
              marginBottom: 22,
            }}
          >
            Your order will be served to this table. Close this dine-in session?
          </p>
          <div style={{ display: "flex", gap: 12 }}>
            <button
              type="button"
              onClick={() => setShowExit(false)}
              style={{
                flex: 1,
                padding: "11px 0",
                borderRadius: 10,
                border: "none",
                background: "#f9bc03",
                color: "var(--color-white)",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Stay
            </button>
            <button
              type="button"
              onClick={closeDineIn}
              style={{
                flex: 1,
                padding: "11px 0",
                borderRadius: 10,
                border: "1px solid var(--color-secondary)",
                background: "#fff",
                color: "var(--color-secondary)",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Close Dine-in
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default DineInBanner;
